/**
 * Cliente HTTP fino sobre `fetch`, compartilhado por todos os módulos de
 * `src/services/api/*`.
 *
 * Decisões que valem a pena lembrar:
 * - A app é servida same-origin sob `/ui/` (ver `app.mount("/ui", ...)` no
 *   backend), então `credentials: "same-origin"` já basta para o cookie de
 *   sessão httpOnly viajar — não há token em header.
 * - O backend devolve erros como `{"detail": "..."}`; extraímos essa chave e
 *   caímos para `HTTP <status>` quando o corpo não é JSON ou não tem detail.
 * - 401 é tratado de forma desacoplada: este módulo NUNCA redireciona.
 *   Quem quiser reagir (o Shell, tipicamente, mandando para /ui/login.html)
 *   chama `onUnauthorized(callback)` uma vez na inicialização da app.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

type UnauthorizedHandler = (path: string) => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Registra o callback disparado sempre que uma resposta 401 chegar por este
 * cliente. Substitui qualquer callback anterior (só faz sentido haver um).
 */
export function onUnauthorized(handler: UnauthorizedHandler): void {
  unauthorizedHandler = handler;
}

export type QueryParams = Record<string, string | number | boolean | undefined | null>;

function buildUrl(path: string, params?: QueryParams): string {
  if (!params) return path;
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    qs.set(key, String(value));
  }
  const query = qs.toString();
  return query ? `${path}?${query}` : path;
}

async function extractErrorDetail(res: Response): Promise<string> {
  try {
    const body: unknown = await res.clone().json();
    if (body && typeof body === "object" && "detail" in body) {
      const detail = (body as { detail: unknown }).detail;
      if (typeof detail === "string") return detail;
      if (detail !== undefined) return JSON.stringify(detail);
    }
  } catch {
    // corpo não era JSON (ou estava vazio) — cai no fallback abaixo.
  }
  return `HTTP ${res.status}`;
}

async function handleResponse<T>(res: Response, path: string): Promise<T> {
  if (res.status === 401) {
    unauthorizedHandler?.(path);
  }
  if (!res.ok) {
    throw new ApiError(res.status, await extractErrorDetail(res));
  }
  if (res.status === 204) {
    return undefined as T;
  }
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

const JSON_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
} as const;

export async function apiGet<T>(path: string, params?: QueryParams): Promise<T> {
  const res = await fetch(buildUrl(path, params), {
    method: "GET",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  return handleResponse<T>(res, path);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: JSON_HEADERS,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return handleResponse<T>(res, path);
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "PATCH",
    credentials: "same-origin",
    headers: JSON_HEADERS,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return handleResponse<T>(res, path);
}

export async function apiDelete<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    method: "DELETE",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  return handleResponse<T>(res, path);
}

/**
 * Envia `FormData` (upload de áudio, imagens, etc). Nunca defina
 * `Content-Type` manualmente com FormData — o browser precisa gerar o
 * boundary do multipart sozinho.
 */
export async function postForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    body: form,
  });
  return handleResponse<T>(res, path);
}
